import { EntryTypeEnum, ProductTypeDto } from 'generated/graphql-gateway';
import { ILocalizedMenuEntry, IndexedMenu, PosServiceMode } from 'state/unified-menu/types';
import {
  getFirstDefaultId,
  hasOptionEntries,
  isComboSlot,
  isItem,
  mapAvailableOptions,
} from 'state/unified-menu/utils';

import { DEFAULT_SLOT_KEY, ModifiersSelections } from '../../product-wizard/types';
import { IModifier, IModifierData, ModifierType } from '../modifier/types';
import {
  getSelectedMultiplier,
  isMultiplierVisuallySelected,
  mapModifier,
} from '../modifier/utils';

import { IModifierGroup } from './product-customization-modifier-group';
import { ComboSlotType, DrinkCategory } from './types';

export function groupModifiersByDisplayName(
  modifiers: IModifier[],
  modifierSelections: ModifiersSelections
): IModifierGroup[] {
  if (modifiers.length === 0) {
    return [];
  }

  const included: IModifierData[] = [];
  const addons: IModifierData[] = [];
  const singleOrMultipleChoice: IModifierData[] = [];

  for (const modifier of modifiers) {
    const modifierData: IModifierData = {
      modifier,
      selections: modifierSelections[modifier.id] || {},
    };

    // Pick selected multiplier or default multiplier if no selection
    const multiplier = getSelectedMultiplier(modifier, modifierData.selections);

    if ([ModifierType.MULTIPLE_CHOICE, ModifierType.SINGLE_CHOICE].includes(modifier.type)) {
      singleOrMultipleChoice.push(modifierData);
    } else if (isMultiplierVisuallySelected(multiplier)) {
      included.push(modifierData);
    } else {
      addons.push(modifierData);
    }
  }

  return [
    { key: 'included', displayGroupName: 'included', modifiers: included },
    { key: 'addons', displayGroupName: 'addons', modifiers: addons },
    { key: 'empty', displayGroupName: undefined, modifiers: singleOrMultipleChoice },
  ];
}

/**
 * Returns the items associated to a combo slot.
 * @param menu - indexed menu to look up related entries
 * @param slotKey - combo slot key to find related items.
 * @returns the items associated to the combo slot.
 */
export function getComboSlotItems(
  menu: IndexedMenu,
  slotKey: string,
  posServiceMode: PosServiceMode
): ILocalizedMenuEntry[] {
  const comboSlot = menu.get(slotKey, { type: EntryTypeEnum.COMBO_SLOT });

  const items = mapAvailableOptions(
    menu,
    posServiceMode,
    comboSlot.options?.entries,
    ({ entryId, price: upsell }) => {
      const item = menu.get(entryId, { type: EntryTypeEnum.ITEM });
      return { ...item, price: upsell ?? item.price };
    }
  );
  return items;
}

/**
 * Returns the type of a combo slot.
 * @param menu - indexed menu to look up related entries
 * @param slotKey - combo slot key.
 * @returns the type of the combo slot.
 */
export function getComboSlotType(menu: IndexedMenu, slotKey: string): ComboSlotType {
  if (slotKey && slotKey !== DEFAULT_SLOT_KEY) {
    if (isDrinkSlot(menu, slotKey)) {
      return ComboSlotType.DRINKS;
    }
    if (isSideSlot(menu, slotKey)) {
      return ComboSlotType.SIDES;
    }
  }
  return ComboSlotType.PICKER_ASPECT;
}

function isDrinkSlot(menu: IndexedMenu, slotKey: string): boolean {
  const comboSlot = menu.get(slotKey, { type: EntryTypeEnum.COMBO_SLOT });
  return comboSlot.productType?.includes(ProductTypeDto.DRINKS) ?? false;
}

function isSideSlot(menu: IndexedMenu, slotKey: string): boolean {
  const comboSlot = menu.get(slotKey, { type: EntryTypeEnum.COMBO_SLOT });
  return comboSlot.productType?.includes(ProductTypeDto.SIDES) ?? false;
}

export const getDrinkCategory = (item: ILocalizedMenuEntry) => {
  if (!item.productType) {
    return DrinkCategory.OTHERS;
  } else if (item.productType.includes(ProductTypeDto.SOFT_DRINKS)) {
    return DrinkCategory.SOFTS;
  } else if (item.productType.includes(ProductTypeDto.SHAKES)) {
    return DrinkCategory.SHAKES;
  } else if (item.productType.includes(ProductTypeDto.FROZEN_DRINKS)) {
    return DrinkCategory.FROZENS;
  }
  return DrinkCategory.OTHERS;
};

/**
 * Whether the entry is customizable.
 * In case of "item" because it has modifiers that requires user interaction.
 * In case of "combo" because it has slots.
 * In case of "comboSlot" because it has multiple slot options.
 * In case of "picker" because it has picker aspects.
 * @param entry - entry to check
 * @returns true if the entry is customizable
 */
export function isCustomizableEntry(
  entry: ILocalizedMenuEntry,
  menu: IndexedMenu,
  posServiceMode: PosServiceMode
): boolean {
  if (isComboSlot(entry)) {
    const slotOptionsCount = entry.options?.entries?.length ?? 0;
    if (slotOptionsCount > 1) {
      return true;
    }
    const item = menu.get(getFirstDefaultId(entry, true));
    return isCustomizableEntry(item, menu, posServiceMode);
  }

  const hasOptions = hasOptionEntries(menu, posServiceMode, entry);
  if (!isItem(entry)) {
    // handles combo, and picker
    return hasOptions;
  }

  if (!hasOptions) {
    // item does not have modifiers
    return false;
  }

  const modifiersCount = entry.options.entries.length;
  return entry.options.entries.some(({ entryId, quantity }) => {
    const mappedModifier = mapModifier(menu, entryId, modifiersCount, posServiceMode, quantity);
    const isSingleOrMultipleChoiceModifier =
      mappedModifier.type === ModifierType.MULTIPLE_CHOICE ||
      mappedModifier.type === ModifierType.SINGLE_CHOICE;
    return mappedModifier.multipliers.length > (isSingleOrMultipleChoiceModifier ? 0 : 1);
  });
}
